import axios from "axios";

// ── Token refresh logic ──────────────────────────────────────────
const REFRESH_MARGIN_MS = 3 * 60 * 1000; // refresh if token expires within 3 min

function tokenExpiresSoon() {
  const token = localStorage.getItem("token");
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const exp = (payload.exp || 0) * 1000;
    return Date.now() > exp - REFRESH_MARGIN_MS;
  } catch {
    return false;
  }
}

let refreshPromise = null;

async function refreshToken() {
  const token = localStorage.getItem("token");
  if (!token) return null;
  try {
    const res = await axios.post(
      `${resolveApiBaseUrl()}/api/auth/refresh`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const newToken = res.data.access_token;
    localStorage.setItem("token", newToken);
    return newToken;
  } catch {
    return null;
  }
}

export async function ensureFreshToken() {
  if (!tokenExpiresSoon()) return localStorage.getItem("token");
  // Deduplicate concurrent refresh calls
  if (!refreshPromise) {
    refreshPromise = refreshToken().finally(() => { refreshPromise = null; });
  }
  return await refreshPromise;
}
// ─────────────────────────────────────────────────────────────────

export function resolveApiBaseUrl() {
  const configured = (process.env.REACT_APP_API_URL || "").trim();
  if (configured) {
    // Normalize configured URL and prevent insecure downgrade on HTTPS pages.
    const normalizedConfigured = configured.replace(/\/+$/, "");
    if (typeof window !== "undefined") {
      try {
        const parsed = new URL(normalizedConfigured, window.location.origin);
        const currentHost = window.location.hostname;
        const isLocalHost = currentHost === "localhost" || currentHost === "127.0.0.1";

        // If frontend is served over HTTPS, never keep an HTTP API URL for same host.
        if (
          window.location.protocol === "https:" &&
          parsed.protocol === "http:" &&
          parsed.hostname === currentHost
        ) {
          return window.location.origin;
        }

        // On remote hosts, avoid explicit frontend port API base that can trigger redirects/CORS.
        if (!isLocalHost && parsed.hostname === currentHost && parsed.port === "3002") {
          return window.location.origin;
        }

        return parsed.origin;
      } catch {
        return normalizedConfigured;
      }
    }
    return normalizedConfigured;
  }

  if (typeof window !== "undefined") {
    // Both the baked Nginx frontend and CRA's development server proxy /api.
    // Same-origin requests avoid CORS differences between blob and JSON calls.
    return window.location.origin;
  }

  return "";
}

const api = axios.create({
  baseURL: resolveApiBaseUrl(),
});

api.interceptors.request.use(async (config) => {
  // Treat every API request as user activity so the auto-logout
  // inactivity timer (20 min) only fires when the user is truly idle.
  try {
    localStorage.setItem("last_activity", new Date().toISOString());
  } catch { /* ignore */ }

  // Skip refresh for the refresh endpoint itself and login
  if (config.url?.includes("/auth/refresh")) return config;

  const token = await ensureFreshToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const method = (config.method || "get").toLowerCase();
  const isWriteMethod = ["post", "put", "patch", "delete"].includes(method);
  if (isWriteMethod) {
    const rawUser = localStorage.getItem("user");
    const user = rawUser ? JSON.parse(rawUser) : null;
    if (user?.role === "viewer") {
      const err = new Error("Viewers can only view data.");
      err.response = {
        status: 403,
        data: { detail: "Viewers can only view data." },
      };
      return Promise.reject(err);
    }
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

export default api;
